import { useTranslation } from 'react-i18next';
import type { Finding, OverviewItem } from '../api-types';
import { kindCounts } from './hotspots';
import { totalsOf } from './findings-view';
import { ProgressBar } from './ProgressBar';

/** The strip over the drives: what to fix first (U8), then one tile per drive with its progress. */
export function SummaryStrip({
  items,
  findings,
}: {
  readonly items: readonly OverviewItem[];
  readonly findings: readonly Finding[];
}) {
  const { t } = useTranslation('archive');
  const totals = totalsOf(items);
  // «21 nome non conforme · 9 cartella non prevista»: the kinds of the findings the page holds.
  const byKind = kindCounts(findings)
    .map(
      ({ kind, count }) =>
        `${count} ${t(`overview.kinds.${kind}`, { defaultValue: kind }).toLocaleLowerCase()}`,
    )
    .join(' · ');
  return (
    <ul
      className="grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3"
      aria-label={t('overview.drives')}
    >
      <li className="bg-muted/40 border-border rounded-md border p-4">
        <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          {t('overview.toFix')}
        </p>
        <p className="text-3xl font-bold">{totals.toFix}</p>
        <p className="text-muted-foreground text-sm">
          {t('overview.toFixSub', { total: totals.total })}
        </p>
        {byKind === '' ? null : <p className="mt-1 text-sm">{byKind}</p>}
      </li>
      {items.map((drive) => {
        const progress = drive.progress ?? { total: 0, inLine: 0 };
        return (
          <li
            key={drive.id}
            className="bg-card border-border flex flex-col gap-2 rounded-md border p-4"
          >
            <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              {drive.name}
            </p>
            <p className="text-3xl font-bold">{t('overview.inLine', progress)}</p>
            <p className="text-muted-foreground text-sm">{t('overview.inLineSub')}</p>
            <ProgressBar {...progress} label={drive.name} />
            <p className="text-muted-foreground text-sm">
              {t('overview.toFixCount', { count: progress.total - progress.inLine })}
            </p>
          </li>
        );
      })}
    </ul>
  );
}
